import { Flex } from '@cleartax/zoids';
import React, { useContext, useState } from 'react';
import moment from 'moment';
import { CardLabel, CardLabelText } from '../invSummaryCard/styles';
import {
  INVCard,
  InvCompany,
  InvDetails,
  InvWorth,
  ShareBtn,
  DnldBtn,
  InvGSTIN,
  ButtonImg,
} from './styles';
import {
  BusinessLevels,
  ErrorToastText,
  InvoiceStatus,
  ToastNature,
} from '../../../common/types';
import { DOWNLOAD_ICON_BLUE, SHARE_ICON_BLUE } from '../../../constants/urls';
import { downloadPdf } from '../../../flows/download-pdf';
import { triggerInvoiceShare } from '../../../flows/webShare';
import { useGlobalState } from '../../context/qrState';
import { track } from '../../../analytics/analytics';
import { EventIDs } from '../../../analytics/events';
import { UserContext } from '../../context/userContext';
import Toast from '../../Toast';

interface InvCardProps {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceStatus: InvoiceStatus;
  buyerName: string;
  totalInvoiceValue: string;
  businessLevel: BusinessLevels;
  businessNumber: string;
  invoiceId: string;
  buyerGstin: string;
  invoiceType: string;
  businessId: string;
}

const InvoiceCard: React.FC<InvCardProps> = ({
  invoiceNumber,
  invoiceDate,
  invoiceStatus,
  buyerName,
  totalInvoiceValue,
  businessLevel,
  businessNumber,
  invoiceId,
  buyerGstin,
  invoiceType,
  businessId,
}) => {
  const [isDisabled, setIsDisabled] = useState(
    invoiceStatus !== InvoiceStatus.GENERATED &&
      invoiceStatus !== InvoiceStatus.CANCELLED
  );
  const [
    showDownloadSuccessLayout,
    setShowDownloadSuccessLayout,
  ] = useGlobalState('DownloadSuccess');

  const { email } = useContext<Record<string, any>>(UserContext);

  const labelDetails = {
    textColor: '',
    labelColor: '',
    borderColor: '',
    labelText: '',
    invNumbWidth: 70,
  };

  const getColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.NOT_GENERATED:
        labelDetails.textColor = '12422E';
        labelDetails.labelColor = '#FFF7CE';
        labelDetails.borderColor = '#EECE33';
        labelDetails.labelText = 'NOT GENERATED';
        labelDetails.invNumbWidth = 67;
        return labelDetails;
      case InvoiceStatus.GENERATED:
        labelDetails.textColor = '#574900';
        labelDetails.labelColor = '#DDFBE2';
        labelDetails.borderColor = '#50CD65';
        labelDetails.labelText = 'GENERATED';
        labelDetails.invNumbWidth = 73;
        return labelDetails;
      case InvoiceStatus.CANCELLED:
        labelDetails.textColor = '#732A00';
        labelDetails.labelColor = '#FFE5D4';
        labelDetails.borderColor = '#FE9D54';
        labelDetails.labelText = 'CANCELLED';
        labelDetails.invNumbWidth = 73;
        return labelDetails;
      case InvoiceStatus.GENERATION_FAILED:
        labelDetails.textColor = '#732A00';
        labelDetails.labelColor = '#FFD7D5';
        labelDetails.borderColor = '#FF685F';
        labelDetails.labelText = 'FAILED';
        labelDetails.invNumbWidth = 80;
        return labelDetails;
    }
  };
  getColor(invoiceStatus);

  const downloadInvoice = async () => {
    setIsDisabled(!isDisabled);
    const payload = {
      email,
    };
    track(EventIDs.INVOICES_DOWNLOAD, payload);
    if (
      invoiceStatus === InvoiceStatus.GENERATED ||
      invoiceStatus === InvoiceStatus.CANCELLED
    ) {
      try {
        await downloadPdf(
          businessLevel,
          businessNumber,
          invoiceId,
          invoiceNumber,
          buyerGstin,
          invoiceType,
          businessId
        );
        setIsDisabled(
          invoiceStatus !== InvoiceStatus.GENERATED &&
            invoiceStatus !== InvoiceStatus.CANCELLED
        );
        setShowDownloadSuccessLayout(!showDownloadSuccessLayout);
      } catch (error) {
        Toast.notify({
          description: ErrorToastText.DOWNLOAD_PDF,
          nature: ToastNature.ERROR,
        });
      }
    }
  };

  const shareInvoice = () => {
    const invoice = {
      invoiceNumber,
      invoiceDate,
      invoiceStatus,
      buyerName,
      totalInvoiceValue,
      businessLevel,
      businessNumber,
    };
    const payload = {
      email,
    };
    track(EventIDs.INVOICES_SHARE, payload);
    try {
      triggerInvoiceShare(invoice);
    } catch (error) {
      Toast.notify({
        description: ErrorToastText.SHARE_INV_DETAILS,
        nature: ToastNature.ERROR,
      });
    }
  };

  return (
    <INVCard>
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        border="1px solid black"
      >
        <InvDetails 
        width ={labelDetails.invNumbWidth }>
          <span
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            Invoice #{invoiceNumber} |{' '}
            {moment(invoiceDate, 'DD/MM/YYYY').format('DD MMM YYYY')}
          </span>
        </InvDetails>
        <CardLabel
          padding={4}
          color={labelDetails.labelColor}
          backgroundColor={labelDetails.borderColor}
          margin={0}
        >
          <CardLabelText color={labelDetails.textColor}>
            {labelDetails.labelText}
          </CardLabelText>
        </CardLabel>
      </Flex>
      <Flex
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="space-between"
          width="66%"
        >
          <InvCompany> {buyerName}</InvCompany>
          <InvGSTIN>{`${businessLevel}: ${businessNumber}`}</InvGSTIN>
        </Flex>
        <InvWorth>
          <span
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            ₹ {totalInvoiceValue}
          </span>
        </InvWorth>
      </Flex>
      <Flex flexDirection="row" alignItems="center" justifyContent="flex-end">
        <ShareBtn onClick={shareInvoice}>
          <ButtonImg src={SHARE_ICON_BLUE} alt="" />
          <span style={{ marginLeft: '-5px' }}>Share</span>
        </ShareBtn>
        <DnldBtn disabled={isDisabled} onClick={downloadInvoice}>
          <ButtonImg src={DOWNLOAD_ICON_BLUE} alt="" />
          <span style={{ marginLeft: '-2px' }}>Download</span>
        </DnldBtn>
      </Flex>
    </INVCard>
  );
};

export default InvoiceCard;
